/**
 * useResendInvitation — TanStack mutation over
 * `POST /organizations/:slug/invitations/:id/resend`.
 *
 * Body: (none). Response (200): `{ id, expires_at }` (a *fresh*
 * invitation; the old one is auto-revoked server-side).
 *
 * Error envelope (tagged union):
 *   - 'unprocessable' — 422; carries `reason` ('already_accepted'
 *                        or 'already_revoked').
 *   - 'forbidden'    — 403 (missing identity.invitations.resend).
 *   - 'notFound'     — 404 (invitation or organization not found).
 *   - 'rateLimited'  — 429 (3/hr per org-invitation pair).
 *   - 'error'        — anything else.
 *
 * Invalidates the shared invitations list + the audit feed on success.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildInvitationsListQueryKey } from './use-create-invitation.js';

export interface ResendInvitationInput {
  readonly invitationId: string;
}

export interface ResendInvitationResult {
  readonly id: string;
  readonly expiresAt: string;
}

export type ResendInvitationError =
  | { readonly kind: 'unprocessable'; readonly reason: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'rateLimited' }
  | { readonly kind: 'error' };

export interface UseResendInvitationResult {
  readonly mutateAsync: (
    input: ResendInvitationInput,
  ) => Promise<ResendInvitationResult>;
  readonly isPending: boolean;
}

export function useResendInvitation(): UseResendInvitationResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    ResendInvitationResult,
    ResendInvitationError,
    ResendInvitationInput
  >({
    mutationFn: async (input) => {
      if (orgSlug === null) {
        throw { kind: 'error' } satisfies ResendInvitationError;
      }
      const { response, data, error } = await apiClient.POST(
        '/organizations/{slug}/invitations/{id}/resend',
        {
          params: { path: { slug: orgSlug, id: input.invitationId } },
        },
      );
      if (response.status === 422) {
        const body = (error ?? data) as { message?: unknown } | undefined;
        const reason =
          typeof body?.message === 'string' ? body.message : 'unprocessable';
        throw {
          kind: 'unprocessable',
          reason,
        } satisfies ResendInvitationError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies ResendInvitationError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies ResendInvitationError;
      }
      if (response.status === 429) {
        throw { kind: 'rateLimited' } satisfies ResendInvitationError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies ResendInvitationError;
      }
      const dto = data as { id: string; expires_at: string };
      return { id: dto.id, expiresAt: dto.expires_at };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: buildInvitationsListQueryKey(orgSlug),
      });
      // Eventually-surfaced `user.invitation_resent` audit event —
      // invalidate broadly across any open audit feed for the org.
      await queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          return (
            Array.isArray(k) &&
            k[0] === 'identity' &&
            k[2] === orgSlug &&
            k.includes('audit')
          );
        },
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
